import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { ChevronsRight } from "lucide-react";
import { useParams } from "react-router-dom";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextArea from "@/common/components/TextArea";
import useRewardItem from "../../hooks/useRewardItem";
import { ShowHeader } from "@/common/components/ShowHeader";
import useUpdateRewardItem from "../../hooks/useUpdateRewardItem";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { RewardItemValidationSchema, createSchema } from "../../schemas/create.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SelectController from "@/common/components/SelectController";
import { ProductBadgesTransformer } from "@/features/productBadges/services/productBadgesTransformer";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { useProductBadges } from "@/common/hooks/queries/useProductBadges";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { dateTransformer } from "@/common/utils/dateTransformer";
import { format } from "date-fns";
import { formatCurrency } from "@/common/utils/formatters";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const Update = () => {
  const { id } = useParams();
  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const [searchProductBadge, setSearchProductBadge] = useDebounce('');

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const {
    data: initialRewardItem,
    isPending: initialRewardItemIsPending,
  } = useRewardItem(id || '');

  const {
    mutate,
    isPending: updateRewardItemIsPending
  } = useUpdateRewardItem({
    id: id || '',
    initialRewardItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewardItem', id] });
      goBack({ fallback: `/tmais-reward-items/${id}` })
    },
    onError: (error) => {
      if (error?.response?.data?.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { data: productsBadgesOptions, isFetched: productsBadgesOptionsIsFetched } = useProductBadges({
    filters: { search: searchProductBadge },
    mapper: Mapper.mapWithTransformer({ transformer: ProductBadgesTransformer.optionsTransformer() }),
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToUpdateTmaisProduct =
    isUserPermissionsPending || hasUserPermission('Establishment::Tmais::Products::Update')

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<RewardItemValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      display_name: initialRewardItem?.display_name,
      description: initialRewardItem?.description,
      available_from: initialRewardItem?.available_from ? dateTransformer(format(initialRewardItem.available_from, 'yyyy-MM-dd')) : undefined,
      available_until: initialRewardItem?.available_until ? dateTransformer(format(initialRewardItem.available_until, 'yyyy-MM-dd')) : undefined,
      sku: initialRewardItem?.sku,
      points_to_redeem: initialRewardItem?.points_to_redeem ? String(initialRewardItem.points_to_redeem) : undefined,
      reference_price: initialRewardItem?.reference_price ? formatCurrency(Number(initialRewardItem?.reference_price)) : undefined,
    }
  });

  useEffect(() => {
    reset({
      pictures_attachments_attributes: initialRewardItem?.pictures?.map((photo) => ({ id: photo.id, file: photo.url })) || [],
      product_badges_attributes: initialRewardItem?.product_badges ? productsBadgesOptions.filter((badge) => initialRewardItem.product_badges.some((initialBadge) => badge?.value === initialBadge?.badge?.id)) : undefined
    })
  }, [productsBadgesOptionsIsFetched])

  const onSubmit: SubmitHandler<RewardItemValidationSchema> = (data) => mutate(data);

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-reward-items` }}
        title={
          <div className="flex gap-2 items-center justify-start">
            <h2 className="text-xl font-bold">Itens para resgate</h2>
            <ChevronsRight color='#ffffffb3' />
            <h2 className="text-xl font-bold line-clamp-1">{initialRewardItem?.display_name}</h2>
          </div>
        }
        buttons={userHasPermissionToUpdateTmaisProduct ? (
          <Button
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
          >
            Salvar
          </Button>
        ) : undefined}
      />

      {initialRewardItemIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={userHasPermissionToUpdateTmaisProduct ? 200 : 403}
        >
          <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <h2 className="text-lg font-bold">Detalhes da Recompensa</h2>

            <div className="flex gap-4">
              <MultipleImageUploader
                label="Anexe uma foto"
                name="equipment-images"
                accept="image/jpeg, image/png"
                containerClassName="max-w-[288px]"
                uploadBlob={uploadBlob}
                control={control}
                fieldName="pictures_attachments_attributes"
                disabled={uploadBlobIsPending || updateRewardItemIsPending}
                errorMessage={errors.pictures_attachments_attributes?.message || undefined}
                uploadButtonLayout="floating"
              />
              <div className="flex flex-col gap-4 w-full">
                <div className="flex items-center gap-2">
                  <TextField
                    label="Nome"
                    {...register('display_name')}
                    errorMessage={errors?.display_name?.message}
                    markAsRequired
                    className="w-1/2"
                  />
                  <TextField
                    label="SKU/Código do produto"
                    {...register('sku')}
                    errorMessage={errors?.sku?.message}
                    className="w-1/2"
                  />
                </div>


                <TextArea
                  label="Descrição"
                  markAsRequired
                  errorMessage={errors?.description?.message}
                  {...register('description')}
                  rows={8}
                />

                <SelectController
                  control={control}
                  name="product_badges_attributes"
                  label="Destacar item com"
                  isMulti
                  onInputChange={setSearchProductBadge}
                  options={productsBadgesOptions || []}
                  errorMessage={errors.product_badges_attributes?.message}
                />

              </div>
            </div>

            <h2 className="text-base font-bold">Valor e Estoque</h2>

            <div className="flex items-center gap-2">
              <Controller
                control={control}
                name='reference_price'
                disabled={updateRewardItemIsPending}
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor referência (em reais)"
                    {...field}
                    markAsRequired
                    errorMessage={errors?.reference_price?.message || undefined}
                    className="w-1/2"
                  />
                )}
              />
              <UnitTextField
                label="Valor para resgate (em pontos)"
                {...register('points_to_redeem')}
                errorMessage={errors?.points_to_redeem?.message}
                defaultValue={initialRewardItem?.points_to_redeem}
                disabled={updateRewardItemIsPending}
                markAsRequired
                decimals={0}
                className="w-1/2"
              />
            </div>

            <div className="flex items-center gap-2">
              <DateInput
                fieldName="available_from"
                control={control}
                errors={errors}
                errorMessage={errors.available_from?.message || undefined}
                label="Data de disponibilidade"
                markAsRequired
                disabled={updateRewardItemIsPending}
                className="w-1/2"
              />
              <DateInput
                fieldName="available_until"
                control={control}
                errors={errors}
                errorMessage={errors.available_until?.message || undefined}
                label="Data de expiração"
                disabled={updateRewardItemIsPending}
                className="w-1/2"
              />
            </div>
          </Box>
        </ErrorMessageWrapper>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Update;
